import React from 'react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Printer, Clock, Play, XCircle, CheckCircle, Minus, FileText, AlertCircle } from 'lucide-react';
import { useLiveEvent } from './LiveEventContext';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const RunningOrderPanel = ({ sessionId, sessionRunningOrder, zoneFilter }) => {
  const {
    t, language, runningOrder, currentStatus, competitions, eventTypes
  } = useLiveEvent();

  const ro = sessionRunningOrder || runningOrder;
  if (!ro || !ro.running_order || ro.running_order.length === 0) return null;

  const session = ro.session || {};
  let passages = [...ro.running_order].sort((a, b) => (a.passage_order || 0) - (b.passage_order || 0));
  if (zoneFilter) {
    passages = passages.filter(p => Number(p.zone_number || 1) === Number(zoneFilter));
  }

  const formatTimeHHMM = (dateValue) => {
    if (!dateValue) return '-';
    try {
      const d = new Date(dateValue);
      if (isNaN(d.getTime())) return '-';
      return d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit', hour12: false });
    } catch { return '-'; }
  };

  const formatAnnouncedPerformance = (passage) => {
    const ap = passage.announced_performance || passage.registration?.announced_performance;
    if (!ap) return '-';
    const eventCode = passage.registration?.event_type_code || session.event_type_code || '';
    if (eventCode === 'STA') {
      const totalSec = Math.round(ap);
      const min = Math.floor(totalSec / 60);
      const sec = totalSec % 60;
      return `${String(min).padStart(2, '0')}:${String(sec).padStart(2, '0')}`;
    }
    return `${ap}m`;
  };

  const formatDuration = (seconds) => {
    if (!seconds && seconds !== 0) return '-';
    const m = Math.floor(seconds / 60);
    const s = Math.round(seconds % 60);
    return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  };

  const formatRP = (passage) => {
    if (passage.distance_covered == null) return '-';
    const evCode = passage.registration?.event_type_code || session.event_type_code || '';
    if (evCode === 'STA') return formatDuration(passage.distance_covered);
    return `${passage.distance_covered}m`;
  };

  const getPassageStatusIcon = (status, card) => {
    if (card === 'RED' || card === 'red') return <XCircle className="text-red-400" size={18} />;
    if (card === 'YELLOW' || card === 'yellow') return <AlertCircle className="text-yellow-400" size={18} />;
    if (card === 'WHITE' || card === 'white' || status === 'completed') return <CheckCircle className="text-green-400" size={18} />;
    if (status === 'performing') return <Play className="text-yellow-400" size={18} />;
    if (status === 'awaiting_results') return <FileText className="text-orange-400" size={18} />;
    if (status === 'dns') return <Minus className="text-gray-400" size={18} />;
    if (status === 'dq') return <XCircle className="text-red-400" size={18} />;
    return <Clock className="text-blue-400" size={18} />;
  };

  const printPDF = () => {
    const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
    const competition = (competitions || []).find(c => c.id === session.competition_id);
    const eventType = (eventTypes || []).find(et => et.code === session.event_type_code);
    let currentY = 12;

    // Enriched Header — Competition title
    if (competition) {
      doc.setFontSize(18); doc.setFont('helvetica', 'bold'); doc.setTextColor(30, 58, 95);
      doc.text(competition.name || '', 148, currentY, { align: 'center' });
      currentY += 6;
    }

    // Federation + dates
    const dateParts = [];
    if (competition?.federation) dateParts.push(competition.federation);
    if (competition?.start_date) {
      const sd = new Date(competition.start_date).toLocaleDateString(language === 'fr' ? 'fr-FR' : 'en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
      const ed = competition.end_date ? new Date(competition.end_date).toLocaleDateString(language === 'fr' ? 'fr-FR' : 'en-GB', { day: 'numeric', month: 'long', year: 'numeric' }) : null;
      dateParts.push(ed && ed !== sd ? `${sd} - ${ed}` : sd);
    }
    if (dateParts.length) {
      doc.setFontSize(11); doc.setFont('helvetica', 'normal'); doc.setTextColor(80, 80, 80);
      doc.text(dateParts.join('  ·  '), 148, currentY, { align: 'center' });
      currentY += 5;
    }

    // Location + pool
    const locParts = [];
    if (competition?.location) locParts.push(competition.location);
    if (competition?.pool_type || competition?.pool_length) {
      locParts.push(`${competition?.pool_type || ''}${competition?.pool_length ? ' ' + competition.pool_length + 'm' : ''}${competition?.lanes ? ' · ' + competition.lanes + ' ' + (language === 'fr' ? 'couloirs' : 'lanes') : ''}`.trim());
    }
    if (locParts.length) {
      doc.setFontSize(10); doc.setTextColor(100, 100, 100);
      doc.text(locParts.join('  ·  '), 148, currentY, { align: 'center' });
      currentY += 5;
    }

    if (competition?.organizer) {
      doc.setFontSize(9); doc.setTextColor(120, 120, 120);
      doc.text((language === 'fr' ? 'Organisateur : ' : 'Organizer: ') + competition.organizer, 148, currentY, { align: 'center' });
      currentY += 5;
    }

    // Event/session row
    if (eventType || session.name) {
      doc.setFontSize(13); doc.setFont('helvetica', 'bold'); doc.setTextColor(30, 58, 95);
      const evLine = `${eventType?.name || session.event_type_code || ''}${session.name ? '  ·  ' + session.name : ''}`;
      doc.text(evLine, 148, currentY, { align: 'center' });
      currentY += 6;
    }

    doc.setDrawColor(30, 58, 95); doc.setLineWidth(0.5); doc.line(10, currentY, 287, currentY); currentY += 4;
    doc.setFontSize(13); doc.setFont('helvetica', 'bold'); doc.setTextColor(30, 58, 95);
    doc.text(language === 'fr' ? 'Ordre de passage' : 'Running Order', 148, currentY, { align: 'center' }); currentY += 7;

    const rows = passages.map(p => {
      const reg = p.registration || {};
      const fd = reg.freediver || {};
      const evCode = reg.event_type_code || session.event_type_code || '';
      const rpDisplay = p.distance_covered != null ? (evCode === 'STA' ? formatDuration(p.distance_covered) : `${p.distance_covered}`) : '-';
      // Single column "OT / Start" : show actual_start_time if present, otherwise official_top_time
      const otStart = p.actual_start_time ? formatTimeHHMM(p.actual_start_time) : formatTimeHHMM(p.official_top_time);
      return [
        p.passage_order || '-',
        (fd.last_name || '').toUpperCase(),
        fd.first_name || '',
        fd.gender === 'male' ? 'M' : fd.gender === 'female' ? 'F' : '-',
        fd.nationality || '-',
        evCode,
        formatAnnouncedPerformance(p),
        formatTimeHHMM(p.warmup_time),
        formatTimeHHMM(p.transition_time),
        formatTimeHHMM(p.countdown_time),
        otStart,
        formatDuration(p.performance_time_seconds),
        rpDisplay,
        p.card_result || '-',
      ];
    });
    autoTable(doc, {
      startY: currentY,
      head: [['#', language === 'fr' ? 'NOM' : 'NAME', language === 'fr' ? 'PRENOM' : 'FIRST', 'S', 'NAT', 'DISC', 'AP', language === 'fr' ? 'ECH.' : 'W-UP', language === 'fr' ? 'TRANS.' : 'TRANS.', language === 'fr' ? 'OFF.' : 'OFF.', 'OT / START', language === 'fr' ? 'DUREE' : 'DUR.', 'RP', language === 'fr' ? 'CARTON' : 'CARD']],
      body: rows,
      styles: { fontSize: 7, cellPadding: 2, font: 'helvetica' },
      headStyles: { fillColor: [30, 58, 95], textColor: [255, 255, 255], fontStyle: 'bold' },
      alternateRowStyles: { fillColor: [240, 245, 255] },
      margin: { left: 5, right: 5 },
    });
    doc.save(`running_order_${session.name || session.id}.pdf`);
  };

  // Detect 2-zone mode and add Z column when relevant
  const showZoneColumn = passages.some(p => p.zone_number && p.zone_number > 0) && (ro.session?.zone_count || 1) > 1 || passages.some(p => Number(p.zone_number) > 1);

  return (
    <div className="bg-blue-950/60 border border-blue-700/30 border-t-0 rounded-b-lg overflow-hidden" data-testid={`running-order-panel-${sessionId}`}>
      <div className="flex items-center justify-between px-4 py-2 bg-blue-900/40 border-b border-blue-700/30">
        <div className="flex items-center gap-2">
          <span className="text-blue-200 font-semibold text-sm">{t('runningOrder') || 'Running Order'} - {session.name}</span>
          <span className="text-blue-400 text-xs">({passages.length}{zoneFilter ? ` ${t('zone') || 'zone'} ${zoneFilter}` : ''} / {ro.total_participants || 0} {t('participants') || 'participants'})</span>
        </div>
        <Button onClick={printPDF} size="sm" variant="outline" className="border-blue-600 text-blue-300 hover:bg-blue-700/30">
          <Printer size={14} className="mr-1" /> PDF
        </Button>
      </div>
      <div className="overflow-x-auto max-h-[700px]">
        <Table>
          <TableHeader>
            <TableRow className="border-blue-700/30">
              <TableHead className="text-blue-300 text-xs">#</TableHead>
              {showZoneColumn && <TableHead className="text-blue-300 text-xs">Z</TableHead>}
              <TableHead className="text-blue-300 text-xs">{t('lastName') || 'Last Name'}</TableHead>
              <TableHead className="text-blue-300 text-xs">{t('firstName') || 'First Name'}</TableHead>
              <TableHead className="text-blue-300 text-xs">{t('nationality') || 'Nat.'}</TableHead>
              <TableHead className="text-blue-300 text-xs">AP</TableHead>
              <TableHead className="text-blue-300 text-xs">{t('warmup') || 'Warm-up'}</TableHead>
              <TableHead className="text-blue-300 text-xs">{t('transit') || 'Transit'}</TableHead>
              <TableHead className="text-blue-300 text-xs">{t('official') || 'Official'}</TableHead>
              <TableHead className="text-blue-300 text-xs font-bold">OT / Start</TableHead>
              <TableHead className="text-blue-300 text-xs">{t('duration') || 'Duration'}</TableHead>
              <TableHead className="text-blue-300 text-xs">RP</TableHead>
              <TableHead className="text-blue-300 text-xs">{t('status') || 'Status'}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {passages.map((passage) => {
              const reg = passage.registration || {};
              const freediver = reg.freediver || {};
              const isCurrentPassage = passage.id === currentStatus?.current_passage?.id;
              const zn = Number(passage.zone_number || 1);
              return (
                <TableRow key={passage.id} className={`border-blue-700/30 ${isCurrentPassage ? 'bg-green-900/30 border-l-2 border-l-green-400' : ''}`}>
                  <TableCell className="text-blue-200 font-mono font-bold text-xs">{passage.passage_order || '-'}</TableCell>
                  {showZoneColumn && (
                    <TableCell>
                      <span className={`inline-flex items-center justify-center w-5 h-5 rounded text-[10px] font-bold ${zn === 1 ? 'bg-blue-600 text-white' : 'bg-emerald-600 text-white'}`}>{zn}</span>
                    </TableCell>
                  )}
                  <TableCell className="text-white font-semibold text-xs">{(freediver.last_name || '').toUpperCase()}</TableCell>
                  <TableCell className="text-blue-100 text-xs">{freediver.first_name || '-'}</TableCell>
                  <TableCell className="text-blue-200 text-xs">{freediver.nationality || '-'}</TableCell>
                  <TableCell className="text-teal-300 font-medium text-xs">{formatAnnouncedPerformance(passage)}</TableCell>
                  <TableCell className="text-blue-200 font-mono text-xs">{formatTimeHHMM(passage.warmup_time)}</TableCell>
                  <TableCell className="text-blue-200 font-mono text-xs">{formatTimeHHMM(passage.transition_time)}</TableCell>
                  <TableCell className="text-blue-200 font-mono text-xs">{formatTimeHHMM(passage.countdown_time)}</TableCell>
                  <TableCell className="text-yellow-300 font-mono font-bold text-xs">{passage.actual_start_time ? formatTimeHHMM(passage.actual_start_time) : formatTimeHHMM(passage.official_top_time)}</TableCell>
                  <TableCell className="text-blue-200 font-mono text-xs">{formatDuration(passage.performance_time_seconds)}</TableCell>
                  <TableCell className="text-green-300 font-medium text-xs">{formatRP(passage)}</TableCell>
                  <TableCell>{getPassageStatusIcon(passage.status, passage.card_result)}</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

export default RunningOrderPanel;
